import type { GraphQLError } from 'graphql';
import { useErrorToast } from '@frameio/shared/resources/shared/hooks';
import { BULK_OPERATION_CHUNK_SIZE } from '../../../bulk-operations/constants';
import {
  useAddBulkAssetOperation,
  useHandleBulkAssetOperationFailed,
} from '../../../bulk-operations/contexts';
import { useCreateDeleteAssetsOperationMutation } from '../../../bulk-operations/gql/generated/CreateDeleteAssetsOperation';
import { useStartDeleteAssetsOperationMutation } from '../../../bulk-operations/gql/generated/StartDeleteAssetsOperation';
import { getOperationErrorMessage } from '../../utils';
import TEXT from './generated/TEXT/useBulkDeleteAssets';

TEXT.DEFINE({
  deleteFailed: (assetCount: number): string =>
    `Failed to delete ${assetCount} items`,
  deleteDenied: "You don't have permission to delete these items.",
});

type BulkDeleteAssetsProps = {
  assetIds: string[];
  onSuccess?: () => void;
  onError?: (error?: readonly GraphQLError[]) => void;
  projectId?: string | null;
};

/**
 * useBulkDeleteAssets is used to delete large batches of assets. On the BE, bulk deletes
 * are processed asynchronously and in chunks, managed by an oban queue. The actual deletingg of assets
 * is not handled in the lifecycle of the request, but rather via subscription events once the assets are processed.
 */
export const useBulkDeleteAssets = () => {
  const addErrorToast = useErrorToast();
  const addBulkAssetOperation = useAddBulkAssetOperation();
  const handlebulkAssetOperationFailed = useHandleBulkAssetOperationFailed();
  const [createDeleteAssetsOperation] =
    useCreateDeleteAssetsOperationMutation();
  const [startDeleteAssetsOperation] = useStartDeleteAssetsOperationMutation();

  return async ({
    assetIds,
    onSuccess,
    onError,
    projectId,
  }: BulkDeleteAssetsProps) => {
    const assetCount = assetIds.length;
    let operationId: string;

    const handleResolve = () => {
      onSuccess?.();
    };

    const handleError = (error?: readonly GraphQLError[]) => {
      handlebulkAssetOperationFailed(operationId, assetCount);
      onError?.(error);
    };

    /**
     * Initiate Delete Asset Operation
     */
    await createDeleteAssetsOperation({
      variables: {
        input: {
          assetIds,
        },
      },
    })
      .then((result) => {
        operationId = result?.data?.createDeleteAssetsOperation?.assetOperation
          ?.id as string;

        if (!operationId || typeof operationId !== 'string') {
          throw new Error('Failed to create delete assets operation');
        }

        addBulkAssetOperation(operationId, {
          operationName: 'delete',
          totalAssetCount: assetCount,
          destination: projectId ? { page: 'trash', projectId } : undefined,
        });

        /**
         * Execute Delete Asset Operation
         */
        return startDeleteAssetsOperation({
          variables: {
            input: {
              assetOperationId: operationId,
              chunkSize: BULK_OPERATION_CHUNK_SIZE,
            },
          },
        }).then(handleResolve, handleError);
      })
      .catch((error) => {
        const errorMessage = getOperationErrorMessage(error, {
          default: TEXT.deleteFailed(assetCount),
          permissionDenied: TEXT.deleteDenied,
        });
        addErrorToast({ title: errorMessage });
        handleError(error);
      });
  };
};
